import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Mail, MessageCircle, Move, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { FacebookIcon, GoogleBusinessIcon, InstagramIcon, LinkedInIcon } from '../PlatformIcon';
import { useBusiness } from '../../contexts/BusinessContext';
import { GBP_ENABLED, SOCIAL_ENABLED } from '../../lib/featureFlags';
import { holidayCountryFromTimezone, holidayNameForDate } from '../../lib/holidays';
import { hapticLight, hapticMedium } from '../../lib/haptics';
import { moveIsoToDate } from '../../lib/postSchedule';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import type { UpcomingGbpPostSnippet } from '../../hooks/useUpcomingGbpPostsSnippet';
import type { UpcomingSocialPostSnippet } from '../../hooks/useUpcomingSocialPostsSnippet';
import CalendarCreateChoiceMenu, {
  monthCreateSlotKey,
  type AnchorRect,
  type CalendarCreateChoiceOption,
} from '../calendar/CalendarCreateChoiceMenu';
import ControlCentreGbpPostPreviewModal from '../admin-dashboard/ControlCentreGbpPostPreviewModal';
import ControlCentreSocialPostPreviewModal from '../admin-dashboard/ControlCentreSocialPostPreviewModal';
import CreateGbpPostModal from '../CreateGbpPostModal';
import CreateSocialPostModal from '../CreateSocialPostModal';
import type { SocialPlatform } from '../SocialConnectButton';

/** Minimal campaign shape for the shared marketing media calendar. */
export type MarketingCalendarCampaign = {
  id: string;
  name: string;
  status: string;
  billing_status?: string | null;
  channel_type?: string | null;
  sent_count?: number | null;
  sent_at?: string | null;
  scheduled_at?: string | null;
  created_at: string;
};

export interface MarketingMediaCalendarProps {
  campaigns: MarketingCalendarCampaign[];
  gbpPosts?: UpcomingGbpPostSnippet[];
  socialPosts?: UpcomingSocialPostSnippet[];
  businessId?: string | null;
  /** Show New Campaign button in the header (default true). */
  showCreateButton?: boolean;
  /** Header "New Campaign" — opens campaign create directly. */
  onCreate?: () => void;
  onCampaignClick?: (campaign: MarketingCalendarCampaign) => void;
  /**
   * When the user picks "Campaign" from the date-click menu.
   * Receives the clicked calendar date (YYYY-MM-DD).
   */
  onCreateCampaign?: (dateStr: string) => void;
  /** @deprecated Prefer onCreateCampaign — still called for campaign option if set. */
  onDateClick?: (dateStr: string) => void;
  onGbpChanged?: () => void;
  onSocialChanged?: () => void;
  className?: string;
  /** Grow to the month’s natural height instead of filling a clipped parent. */
  autoHeight?: boolean;
  /**
   * Stretch week rows to fill the parent's height (marketing home) and show
   * EVERY item per day — no "+N more" cap; the page scrolls if rows grow.
   */
  fillHeight?: boolean;
}


type CreateChoice = 'campaign' | 'gbp' | 'facebook' | 'instagram' | 'linkedin';

type ConnectedChannels = {
  gbp: boolean;
  facebook: boolean;
  instagram: boolean;
  linkedin: boolean;
};

type CalendarItem =
  | {
      id: string;
      kind: 'campaign';
      dateStr: string;
      bgColor: string;
      campaign: MarketingCalendarCampaign;
    }
  | {
      id: string;
      kind: 'gbp';
      dateStr: string;
      bgColor: string;
      gbpPost: UpcomingGbpPostSnippet;
    }
  | {
      id: string;
      kind: 'social';
      dateStr: string;
      bgColor: string;
      socialPost: UpcomingSocialPostSnippet;
    };

/** Staged (pending) Google / social post that can be dragged to another day. */
type MovableItem = Extract<CalendarItem, { kind: 'gbp' | 'social' }>;

type MonthCell = {
  dateStr: string;
  day: number;
  inMonth: boolean;
  isToday: boolean;
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MOBILE_DAY_MAX = 2;
const DESKTOP_DAY_MAX = 3;
/** Press-and-hold duration before a staged post enters move mode. */
const HOLD_TO_MOVE_MS = 450;
/** Pointer travel (px) that cancels a pending hold — treated as a scroll/click. */
const HOLD_CANCEL_PX = 10;

function isPendingMovable(item: CalendarItem): item is MovableItem {
  if (item.kind === 'gbp') return item.gbpPost.status !== 'published';
  if (item.kind === 'social') return item.socialPost.status !== 'published';
  return false;
}

function movableScheduledIso(item: MovableItem): string {
  return item.kind === 'gbp'
    ? item.gbpPost.scheduled_for || item.gbpPost.created_at
    : item.socialPost.scheduled_for || item.socialPost.created_at;
}

function movableDraftId(item: MovableItem): string {
  return item.kind === 'gbp' ? item.gbpPost.id : item.socialPost.id;
}

function movableLabel(item: MovableItem): string {
  const content = item.kind === 'gbp' ? item.gbpPost.content : item.socialPost.content;
  const text = String(content || '').replace(/\s+/g, ' ').trim();
  if (text) return text.length > 48 ? `${text.slice(0, 48)}…` : text;
  if (item.kind === 'gbp') return 'Google Business post';
  const p = item.socialPost.platform;
  return p === 'instagram' ? 'Instagram post' : p === 'linkedin' ? 'LinkedIn post' : 'Facebook post';
}

function dateKeyFromPoint(x: number, y: number): { dateStr: string; el: HTMLElement } | null {
  if (typeof document === 'undefined') return null;
  const hit = document.elementFromPoint(x, y) as HTMLElement | null;
  const cell = hit?.closest?.('[data-date]') as HTMLElement | null;
  const dateStr = cell?.getAttribute('data-date');
  return cell && dateStr ? { dateStr, el: cell } : null;
}

function dateStrToScheduleLocal(dateStr: string): string {
  return `${dateStr}T12:00`;
}

function formatChoiceDate(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** True when dateStr (YYYY-MM-DD) is before local today. */
function isPastCalendarDay(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoToLocalDateKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return toDateKey(d);
}

function todayKey(): string {
  return toDateKey(new Date());
}

/** Monday-first month cells; trailing empty weeks omitted. */
function buildMonthCells(viewDate: Date): MonthCell[] {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);
  const today = todayKey();

  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    const dateStr = toDateKey(d);
    cells.push({
      dateStr,
      day: d.getDate(),
      inMonth: d.getMonth() === month,
      isToday: dateStr === today,
    });
  }

  while (cells.length > 28) {
    const lastWeek = cells.slice(-7);
    if (lastWeek.every((c) => !c.inMonth)) cells.splice(-7);
    else break;
  }

  return cells;
}

function rectFromDayEl(dayEl: HTMLElement | null | undefined): AnchorRect | null {
  if (!dayEl?.getBoundingClientRect) return null;
  const rect = dayEl.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function campaignColors(campaign: MarketingCalendarCampaign): string {
  const billingFailed = campaign.billing_status === 'failed';
  const isFailed = campaign.status === 'failed' || billingFailed;
  switch (isFailed ? 'failed' : campaign.status) {
    case 'sent':
    case 'sending':
      return '#10B981';
    case 'scheduled':
      return '#3B82F6';
    case 'failed':
      return '#EF4444';
    case 'paused':
      return '#F59E0B';
    default:
      return '#6B7280';
  }
}

/**
 * Shared month calendar for campaigns + staged Google / Facebook / Instagram posts.
 * Custom CSS grid (not FullCalendar) so booking-calendar viewport CSS cannot stretch it.
 */
export default function MarketingMediaCalendar({
  campaigns,
  gbpPosts = [],
  socialPosts = [],
  businessId,
  showCreateButton = true,
  onCreate,
  onCampaignClick,
  onCreateCampaign,
  onDateClick,
  onGbpChanged,
  onSocialChanged,
  className = '',
  autoHeight = false,
  fillHeight = false,
}: MarketingMediaCalendarProps) {
  const { selectedBusiness } = useBusiness();
  const holidayCountry = useMemo(
    () => holidayCountryFromTimezone(selectedBusiness?.timezone),
    [selectedBusiness?.timezone],
  );
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [detailsGbpPost, setDetailsGbpPost] = useState<UpcomingGbpPostSnippet | null>(null);
  const [detailsSocialPost, setDetailsSocialPost] = useState<UpcomingSocialPostSnippet | null>(
    null,
  );
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );

  const [choiceOpen, setChoiceOpen] = useState(false);
  const [choiceDate, setChoiceDate] = useState<string | null>(null);
  const [choiceAnchorRect, setChoiceAnchorRect] = useState<AnchorRect | null>(null);
  const [choiceAnchorKey, setChoiceAnchorKey] = useState<string | null>(null);
  const [pastRejectRect, setPastRejectRect] = useState<AnchorRect | null>(null);
  const pastRejectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [channels, setChannels] = useState<ConnectedChannels>({
    gbp: false,
    facebook: false,
    instagram: false,
  });
  const [loadingChannels, setLoadingChannels] = useState(false);

  const [createGbpOpen, setCreateGbpOpen] = useState(false);
  const [createSocialPlatform, setCreateSocialPlatform] = useState<SocialPlatform | null>(null);
  const [createScheduleLocal, setCreateScheduleLocal] = useState<string | null>(null);

  // ── Press-and-hold move mode (drag a staged post to another day) ──
  const [movingItem, setMovingItem] = useState<MovableItem | null>(null);
  const movingItemRef = useRef<MovableItem | null>(null);
  const [dragPoint, setDragPoint] = useState<{ x: number; y: number } | null>(null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const hoverDateRef = useRef<string | null>(null);
  /** Optimistic schedule overrides keyed by draft id until the refetch catches up. */
  const [scheduleOverrides, setScheduleOverrides] = useState<Record<string, string>>({});
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdCleanupRef = useRef<(() => void) | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep the open details modal in sync when the underlying post is refetched
  // (e.g. after a drag move or a realtime update changes scheduled_for).
  useEffect(() => {
    setDetailsSocialPost((cur) => {
      if (!cur) return cur;
      const next = socialPosts.find((p) => p.id === cur.id && p.status === cur.status);
      return next && next !== cur ? next : cur;
    });
  }, [socialPosts]);
  useEffect(() => {
    setDetailsGbpPost((cur) => {
      if (!cur) return cur;
      const next = gbpPosts.find((p) => p.id === cur.id && p.status === cur.status);
      return next && next !== cur ? next : cur;
    });
  }, [gbpPosts]);

  // Drop an optimistic override once the server data matches (or the post is gone).
  useEffect(() => {
    setScheduleOverrides((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const id of ids) {
        const post =
          socialPosts.find((p) => p.id === id && p.status !== 'published') ||
          gbpPosts.find((p) => p.id === id && p.status !== 'published');
        const serverMs = post?.scheduled_for ? new Date(post.scheduled_for).getTime() : NaN;
        if (!post || serverMs === new Date(prev[id]).getTime()) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [socialPosts, gbpPosts]);

  const refreshChannels = useCallback(async () => {
    if (!businessId) {
      setChannels({ gbp: false, facebook: false, instagram: false, linkedin: false });
      return;
    }
    setLoadingChannels(true);
    try {
      const [gbpRes, socialRes] = await Promise.all([
        GBP_ENABLED
          ? supabase
              .from('google_business_connections')
              .select('id')
              .eq('business_id', businessId)
              .in('provider', ['ayrshare', 'google'])
              .eq('is_active', true)
              .limit(1)
          : Promise.resolve({ data: [] as { id: string }[] }),
        SOCIAL_ENABLED
          ? supabase
              .from('social_media_connections')
              .select('id, platform')
              .eq('business_id', businessId)
              .eq('provider', 'ayrshare')
              .eq('is_active', true)
          : Promise.resolve({ data: [] as { id: string; platform: string }[] }),
      ]);

      const socialRows = socialRes.data || [];
      setChannels({
        gbp: (gbpRes.data || []).length > 0,
        facebook: socialRows.some((r) => r.platform === 'facebook'),
        instagram: socialRows.some((r) => r.platform === 'instagram'),
        linkedin: socialRows.some((r) => r.platform === 'linkedin'),
      });
    } catch (err) {
      console.error('Failed to load connected marketing channels:', err);
      setChannels({ gbp: false, facebook: false, instagram: false, linkedin: false });
    } finally {
      setLoadingChannels(false);
    }
  }, [businessId]);

  useEffect(() => {
    void refreshChannels();
  }, [refreshChannels]);

  const clearPastReject = useCallback(() => {
    if (pastRejectTimerRef.current) {
      clearTimeout(pastRejectTimerRef.current);
      pastRejectTimerRef.current = null;
    }
    setPastRejectRect(null);
  }, []);

  const closeCreateMenu = useCallback(() => {
    setChoiceOpen(false);
    setChoiceDate(null);
    setChoiceAnchorRect(null);
    setChoiceAnchorKey(null);
  }, []);

  const showPastReject = useCallback(
    (anchorRect: AnchorRect | null) => {
      closeCreateMenu();
      if (pastRejectTimerRef.current) {
        clearTimeout(pastRejectTimerRef.current);
        pastRejectTimerRef.current = null;
      }
      if (!anchorRect) {
        setPastRejectRect(null);
        return;
      }
      setPastRejectRect(anchorRect);
      void hapticMedium();
      pastRejectTimerRef.current = setTimeout(() => {
        setPastRejectRect(null);
        pastRejectTimerRef.current = null;
      }, 900);
    },
    [closeCreateMenu],
  );

  useEffect(() => {
    return () => {
      if (pastRejectTimerRef.current) clearTimeout(pastRejectTimerRef.current);
    };
  }, []);

  const openCreateMenu = useCallback(
    (dateStr: string, anchorRect?: AnchorRect | null) => {
      if (isPastCalendarDay(dateStr)) {
        showPastReject(anchorRect ?? null);
        return;
      }
      clearPastReject();
      const clicked = new Date(`${dateStr}T12:00:00`);
      setChoiceDate(dateStr);
      setChoiceAnchorRect(anchorRect ?? null);
      setChoiceAnchorKey(
        Number.isNaN(clicked.getTime()) ? `month:${dateStr}` : monthCreateSlotKey(clicked),
      );
      setChoiceOpen(true);
      void hapticLight();
      void refreshChannels();
    },
    [refreshChannels, showPastReject, clearPastReject],
  );

  const handleCreateChoice = useCallback(
    (choice: CreateChoice) => {
      if (!choiceDate) return;
      const scheduleLocal = dateStrToScheduleLocal(choiceDate);
      closeCreateMenu();

      if (choice === 'campaign') {
        onCreateCampaign?.(choiceDate);
        onDateClick?.(choiceDate);
        if (!onCreateCampaign && !onDateClick) onCreate?.();
        return;
      }
      if (choice === 'gbp') {
        setCreateScheduleLocal(scheduleLocal);
        setCreateGbpOpen(true);
        return;
      }
      setCreateScheduleLocal(scheduleLocal);
      setCreateSocialPlatform(choice);
    },
    [choiceDate, closeCreateMenu, onCreateCampaign, onDateClick, onCreate],
  );

  const setMoving = useCallback((item: MovableItem | null) => {
    movingItemRef.current = item;
    setMovingItem(item);
  }, []);

  const setHover = useCallback((dateStr: string | null) => {
    if (hoverDateRef.current === dateStr) return;
    hoverDateRef.current = dateStr;
    setHoverDate(dateStr);
  }, []);

  const cancelHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    holdCleanupRef.current?.();
    holdCleanupRef.current = null;
  }, []);

  const exitMoveMode = useCallback(() => {
    cancelHold();
    setMoving(null);
    setDragPoint(null);
    setHover(null);
  }, [cancelHold, setMoving, setHover]);

  useEffect(() => {
    return () => cancelHold();
  }, [cancelHold]);

  useEffect(() => {
    if (!movingItem) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitMoveMode();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [movingItem, exitMoveMode]);

  /** Persist a staged post onto a new day, keeping its clock time. */
  const moveItemToDate = useCallback(
    async (item: MovableItem, dateStr: string, anchorEl?: HTMLElement | null) => {
      exitMoveMode();
      if (dateStr === item.dateStr) return;

      const next = moveIsoToDate(movableScheduledIso(item), dateStr);
      if (!next) {
        toast.error('Could not work out the new schedule for that post');
        return;
      }
      if (next.getTime() <= Date.now()) {
        showPastReject(rectFromDayEl(anchorEl));
        toast.error(
          isPastCalendarDay(dateStr)
            ? "You can't move a post into the past"
            : 'That time has already passed today — change the time first',
        );
        return;
      }
      if (!businessId) {
        toast.error('Select a business first');
        return;
      }

      const draftId = movableDraftId(item);
      const nextIso = next.toISOString();
      setScheduleOverrides((prev) => ({ ...prev, [draftId]: nextIso }));
      void hapticLight();

      try {
        const fn = item.kind === 'gbp' ? 'gbp-auto-post-action' : 'social-auto-post-action';
        const body: Record<string, unknown> = {
          action: 'update',
          businessId,
          draftId,
          scheduledFor: nextIso,
        };
        if (item.kind === 'gbp') {
          body.connectionId = item.gbpPost.connection_id;
        } else {
          body.connectionId = item.socialPost.connection_id;
          body.platform = item.socialPost.platform;
        }
        const { data, error } = await supabase.functions.invoke(fn, { body });
        if (error || data?.error) {
          throw new Error(
            data?.error || (await invokeErrorMessage(error, data)) || 'Failed to move post',
          );
        }
        toast.success(
          `Moved to ${formatChoiceDate(dateStr)} · ${next.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}`,
        );
        if (item.kind === 'gbp') onGbpChanged?.();
        else onSocialChanged?.();
      } catch (err) {
        setScheduleOverrides((prev) => {
          const copy = { ...prev };
          delete copy[draftId];
          return copy;
        });
        void hapticMedium();
        toast.error(err instanceof Error ? err.message : 'Failed to move post');
      }
    },
    [businessId, exitMoveMode, onGbpChanged, onSocialChanged, showPastReject],
  );

  /**
   * Pointer-down on a staged post: start the hold timer. Once it fires the
   * post enters move mode — keep holding and drag to a day, or let go and tap
   * a day. Small movement before the timer fires cancels (scroll / click).
   */
  const beginHold = useCallback(
    (e: React.PointerEvent<HTMLElement>, item: MovableItem) => {
      if (movingItemRef.current) return; // tap-to-drop handled by click handlers
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      cancelHold();
      closeCreateMenu();

      const pointerId = e.pointerId;
      const startX = e.clientX;
      const startY = e.clientY;
      let armed = false;

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        if (!armed) {
          if (Math.hypot(ev.clientX - startX, ev.clientY - startY) > HOLD_CANCEL_PX) cancelHold();
          return;
        }
        if (ev.cancelable) ev.preventDefault();
        setDragPoint({ x: ev.clientX, y: ev.clientY });
        setHover(dateKeyFromPoint(ev.clientX, ev.clientY)?.dateStr ?? null);
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        const wasArmed = armed;
        cancelHold();
        if (!wasArmed) return;
        // The synthetic click (if any) lands right after pointerup; don't let a
        // stale flag swallow a later, unrelated click.
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 400);
        setDragPoint(null);
        const target = dateKeyFromPoint(ev.clientX, ev.clientY);
        setHover(null);
        const moved = Math.hypot(ev.clientX - startX, ev.clientY - startY) > HOLD_CANCEL_PX;
        if (moved && target && target.dateStr !== item.dateStr) {
          void moveItemToDate(item, target.dateStr, target.el);
        }
        // Released without a drop: stay in move mode so a tap on a day finishes it.
      };
      const onCancel = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        cancelHold();
        suppressClickRef.current = false;
        setDragPoint(null);
        setHover(null);
      };

      document.addEventListener('pointermove', onMove, { capture: true, passive: false });
      document.addEventListener('pointerup', onUp, true);
      document.addEventListener('pointercancel', onCancel, true);
      holdCleanupRef.current = () => {
        document.removeEventListener('pointermove', onMove, true);
        document.removeEventListener('pointerup', onUp, true);
        document.removeEventListener('pointercancel', onCancel, true);
      };

      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        armed = true;
        suppressClickRef.current = true;
        setMoving(item);
        setDragPoint({ x: startX, y: startY });
        setHover(item.dateStr);
        void hapticMedium();
      }, HOLD_TO_MOVE_MS);
    },
    [cancelHold, closeCreateMenu, moveItemToDate, setHover, setMoving],
  );

  const calendarItems = useMemo((): CalendarItem[] => {
    const campaignItems: CalendarItem[] = campaigns.map((campaign) => {
      const date = campaign.sent_at || campaign.scheduled_at || campaign.created_at;
      return {
        id: campaign.id,
        kind: 'campaign',
        dateStr: isoToLocalDateKey(date),
        bgColor: campaignColors(campaign),
        campaign,
      };
    });

    const gbpItems: CalendarItem[] = GBP_ENABLED
      ? gbpPosts.map((post) => {
          const published = post.status === 'published';
          return {
            id: `gbp:${post.status}:${post.id}`,
            kind: 'gbp' as const,
            // Published posts land on the day they went live; staged use schedule.
            dateStr: isoToLocalDateKey(
              published
                ? post.created_at
                : scheduleOverrides[post.id] || post.scheduled_for || post.created_at,
            ),
            // Green = completed/published (same as sent campaigns).
            bgColor: published ? '#10B981' : '#F59E0B',
            gbpPost: post,
          };
        })
      : [];

    const socialItems: CalendarItem[] = SOCIAL_ENABLED
      ? socialPosts.map((post) => {
          const published = post.status === 'published';
          const bgColor = published
            ? '#10B981'
            : post.platform === 'instagram'
              ? '#E1306C'
              : post.platform === 'linkedin'
                ? '#0A66C2'
                : '#1877F2';
          return {
            id: `social:${post.status}:${post.platform}:${post.id}`,
            kind: 'social' as const,
            // Published posts land on the day they went live; staged use schedule.
            dateStr: isoToLocalDateKey(
              published
                ? post.created_at
                : scheduleOverrides[post.id] || post.scheduled_for || post.created_at,
            ),
            bgColor,
            socialPost: post,
          };
        })
      : [];

    return [...campaignItems, ...gbpItems, ...socialItems];
  }, [campaigns, gbpPosts, socialPosts, scheduleOverrides]);

  const itemsByDate = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    for (const item of calendarItems) {
      const list = map.get(item.dateStr);
      if (list) list.push(item);
      else map.set(item.dateStr, [item]);
    }
    return map;
  }, [calendarItems]);

  const monthCells = useMemo(() => buildMonthCells(calendarDate), [calendarDate]);

  const handleCalendarPrev = useCallback(() => {
    setCalendarDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }, []);

  const handleCalendarNext = useCallback(() => {
    setCalendarDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }, []);

  const handleCalendarToday = useCallback(() => {
    setCalendarDate(new Date());
  }, []);

  const calendarMonthLabel = useMemo(() => {
    return calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [calendarDate]);

  const dayMax = isNarrow ? MOBILE_DAY_MAX : DESKTOP_DAY_MAX;
  const iconSize = { width: isNarrow ? 10 : 12, height: isNarrow ? 10 : 12 };

  const choiceOptions = useMemo((): CalendarCreateChoiceOption[] => {
    const opts: CalendarCreateChoiceOption[] = [
      {
        id: 'campaign',
        label: 'Campaign',
        description: 'Create a marketing campaign',
        icon: Mail,
        onSelect: () => handleCreateChoice('campaign'),
      },
    ];
    if (GBP_ENABLED && channels.gbp) {
      opts.push({
        id: 'gbp',
        label: 'Google Business post',
        description: 'Stage a Google Business post',
        icon: GoogleBusinessIcon,
        imageIcon: true,
        onSelect: () => handleCreateChoice('gbp'),
      });
    }
    if (SOCIAL_ENABLED && channels.facebook) {
      opts.push({
        id: 'facebook',
        label: 'Facebook post',
        description: 'Stage a Facebook post',
        icon: FacebookIcon,
        imageIcon: true,
        onSelect: () => handleCreateChoice('facebook'),
      });
    }
    if (SOCIAL_ENABLED && channels.instagram) {
      opts.push({
        id: 'instagram',
        label: 'Instagram post',
        description: 'Stage an Instagram post',
        icon: InstagramIcon,
        imageIcon: true,
        onSelect: () => handleCreateChoice('instagram'),
      });
    }
    if (SOCIAL_ENABLED && channels.linkedin) {
      opts.push({
        id: 'linkedin',
        label: 'LinkedIn post',
        description: 'Stage a LinkedIn post',
        icon: LinkedInIcon,
        imageIcon: true,
        onSelect: () => handleCreateChoice('linkedin'),
      });
    }
    return opts;
  }, [channels, handleCreateChoice]);

  const footerNote =
    !loadingChannels && !channels.gbp && !channels.facebook && !channels.instagram && !channels.linkedin
      ? 'Connect Google, Facebook, or Instagram in Media Manager to stage posts from the calendar.'
      : null;

  /**
   * Shared behaviour for staged-post chips: press-and-hold to pick up, click
   * to open details, and tap-to-drop while another post is being moved.
   */
  const movableChipProps = (item: MovableItem, open: () => void) => {
    const isMoving = movingItem?.id === item.id;
    const otherMoving = !!movingItem && !isMoving;
    return {
      onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => beginHold(e, item),
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        const moving = movingItemRef.current;
        if (moving) {
          const cell = (e.currentTarget as HTMLElement).closest('[data-date]') as HTMLElement | null;
          void moveItemToDate(moving, item.dateStr, cell);
          return;
        }
        open();
      },
      className: `flex w-full items-center gap-1 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-white touch-manipulation select-none ${
        isMoving ? 'opacity-40 ring-2 ring-white ring-offset-1 ring-offset-blue-400' : ''
      } ${otherMoving ? 'opacity-70' : ''}`,
      style: {
        backgroundColor: item.bgColor,
        fontSize: isNarrow ? 10 : 12,
        lineHeight: 1.3,
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        touchAction: isMoving ? 'none' : undefined,
        cursor: movingItem ? 'copy' : 'pointer',
      } as React.CSSProperties,
    };
  };

  const renderItem = (item: CalendarItem) => {
    if (item.kind === 'gbp') {
      const label =
        String(item.gbpPost.content || '')
          .replace(/\s+/g, ' ')
          .trim() || 'Google Business post';
      return (
        <button
          key={item.id}
          type="button"
          {...(isPendingMovable(item)
            ? movableChipProps(item, () => setDetailsGbpPost(item.gbpPost))
            : {
                onClick: (e: React.MouseEvent) => {
                  if (movingItemRef.current) return; // let the day cell take the drop
                  e.stopPropagation();
                  setDetailsGbpPost(item.gbpPost);
                },
                className:
                  'flex w-full items-center gap-1 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-white touch-manipulation',
                style: {
                  backgroundColor: item.bgColor,
                  fontSize: isNarrow ? 10 : 12,
                  lineHeight: 1.3,
                  WebkitTapHighlightColor: 'transparent',
                } as React.CSSProperties,
              })}
        >
          <GoogleBusinessIcon className="flex-shrink-0" style={iconSize} />
          <span className="truncate font-medium">{label}</span>
        </button>
      );
    }

    if (item.kind === 'social') {
      const platform = item.socialPost.platform;
      const PlatformIcon =
        platform === 'instagram' ? InstagramIcon : platform === 'linkedin' ? LinkedInIcon : FacebookIcon;
      const imageCount = item.socialPost.image_count || 0;
      const channelName =
        platform === 'instagram' ? 'Instagram' : platform === 'linkedin' ? 'LinkedIn' : 'Facebook';
      const label =
        String(item.socialPost.content || '')
          .replace(/\s+/g, ' ')
          .trim() ||
        (imageCount > 1 ? `${channelName} · ${imageCount} photos` : `${channelName} post`);
      return (
        <button
          key={item.id}
          type="button"
          {...(isPendingMovable(item)
            ? movableChipProps(item, () => setDetailsSocialPost(item.socialPost))
            : {
                onClick: (e: React.MouseEvent) => {
                  if (movingItemRef.current) return; // let the day cell take the drop
                  e.stopPropagation();
                  setDetailsSocialPost(item.socialPost);
                },
                className:
                  'flex w-full items-center gap-1 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-white touch-manipulation',
                style: {
                  backgroundColor: item.bgColor,
                  fontSize: isNarrow ? 10 : 12,
                  lineHeight: 1.3,
                  WebkitTapHighlightColor: 'transparent',
                } as React.CSSProperties,
              })}
        >
          <PlatformIcon className="flex-shrink-0" style={iconSize} />
          <span className="truncate font-medium">{label}</span>
          {imageCount > 1 ? (
            <span className="ml-auto shrink-0 rounded bg-black/20 px-1 text-[10px] font-semibold">
              {imageCount}
            </span>
          ) : null}
        </button>
      );
    }

    const isEmail = item.campaign.channel_type !== 'sms';
    return (
      <button
        key={item.id}
        type="button"
        onClick={(e) => {
          if (movingItemRef.current) return; // let the day cell take the drop
          e.stopPropagation();
          onCampaignClick?.(item.campaign);
        }}
        className="flex w-full items-center gap-1 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-white touch-manipulation"
        style={{
          backgroundColor: item.bgColor,
          fontSize: isNarrow ? 10 : 12,
          lineHeight: 1.3,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {isEmail ? (
          <Mail className="flex-shrink-0" style={iconSize} />
        ) : (
          <MessageCircle className="flex-shrink-0" style={iconSize} />
        )}
        <span className="truncate font-medium">{item.campaign.name}</span>
        {!isNarrow && (item.campaign.sent_count || 0) > 0 ? (
          <span className="ml-auto shrink-0 text-[10px] text-white/70">
            {item.campaign.sent_count}
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <div
      className={`flex flex-col ${
        fillHeight ? 'h-full min-h-0 w-full flex-1' : autoHeight ? 'h-auto w-full' : 'h-full min-h-0'
      } ${className}`}
    >
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200/60 bg-white shrink-0">
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
          <button
            type="button"
            onClick={handleCalendarPrev}
            className="bg-gray-100/80 hover:bg-gray-200/80 rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 transition-all duration-200 flex items-center justify-center active:scale-95"
            style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}
          >
            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
          </button>
          <button
            type="button"
            onClick={handleCalendarToday}
            className="bg-gray-100/80 hover:bg-gray-200/80 rounded-xl px-3 py-1.5 sm:px-4 sm:py-2 transition-all duration-200 text-sm font-semibold text-gray-700 active:scale-95"
            style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}
          >
            Today
          </button>
          <button
            type="button"
            onClick={handleCalendarNext}
            className="bg-gray-100/80 hover:bg-gray-200/80 rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 transition-all duration-200 flex items-center justify-center active:scale-95"
            style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}
          >
            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
          </button>
          <h2
            className="text-base sm:text-lg font-bold text-gray-900 ml-1 sm:ml-2 truncate"
            style={{ letterSpacing: '-0.02em' }}
          >
            {calendarMonthLabel}
          </h2>
        </div>

        {showCreateButton && onCreate ? (
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              type="button"
              onClick={onCreate}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 active:bg-blue-700 text-white rounded-xl transition-all duration-150 font-semibold flex items-center gap-1.5 text-sm shadow-sm active:scale-95"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New Campaign</span>
            </button>
          </div>
        ) : null}
      </div>

      <div
        className={
          fillHeight
            ? 'flex-1 flex flex-col min-h-0 p-2 sm:p-3'
            : autoHeight
              ? 'p-2 sm:p-3 shrink-0'
              : 'flex-1 p-2 sm:p-3 overflow-auto min-h-0'
        }
      >
        <div
          id="marketing-media-calendar"
          className={`marketing-calendar w-full ${fillHeight ? 'flex-1 flex flex-col min-h-0' : ''}`}
          style={{
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
          }}
        >
          <div
            className={`grid w-full grid-cols-7 border border-gray-200 rounded-xl overflow-hidden bg-white ${
              fillHeight ? 'flex-1' : ''
            }`}
            style={
              fillHeight
                ? {
                    // Header row hugs content; week rows share the remaining
                    // height equally but still grow when a day has many items.
                    gridTemplateRows: `auto repeat(${monthCells.length / 7}, minmax(${
                      isNarrow ? 72 : 96
                    }px, 1fr))`,
                  }
                : undefined
            }
          >
            {WEEKDAYS.map((label) => (
              <div
                key={label}
                className="bg-gray-50 px-0.5 py-1.5 sm:py-2 text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200"
              >
                {label}
              </div>
            ))}

            {monthCells.map((cell) => {
              const dayItems = itemsByDate.get(cell.dateStr) || [];
              // Fill mode shows everything — the page scrolls instead of capping.
              const visible = fillHeight ? dayItems : dayItems.slice(0, dayMax);
              const overflow = dayItems.length - visible.length;
              const selected = choiceOpen && choiceDate === cell.dateStr;
              const holidayName = holidayNameForDate(cell.dateStr, holidayCountry);
              const dropHover = !!movingItem && hoverDate === cell.dateStr;
              const dropPast = dropHover && isPastCalendarDay(cell.dateStr);
              const dropSource = !!movingItem && movingItem.dateStr === cell.dateStr;

              return (
                <div
                  key={cell.dateStr}
                  role="button"
                  tabIndex={0}
                  data-date={cell.dateStr}
                  onClick={(e) => {
                    const el = e.currentTarget;
                    const moving = movingItemRef.current;
                    if (moving) {
                      void moveItemToDate(moving, cell.dateStr, el);
                      return;
                    }
                    openCreateMenu(cell.dateStr, rectFromDayEl(el));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      const moving = movingItemRef.current;
                      if (moving) {
                        void moveItemToDate(moving, cell.dateStr, e.currentTarget);
                        return;
                      }
                      openCreateMenu(cell.dateStr, rectFromDayEl(e.currentTarget));
                    }
                  }}
                  className={`min-h-[44px] sm:min-h-[72px] p-0.5 sm:p-1 text-left align-top border-b border-r border-gray-100 touch-manipulation transition-colors ${
                    movingItem ? 'cursor-copy' : 'cursor-pointer'
                  } ${cell.isToday ? 'bg-blue-50' : 'bg-white'} ${
                    selected ? 'ring-2 ring-inset ring-blue-400' : ''
                  } ${!cell.inMonth ? 'bg-gray-50/60' : ''} ${
                    dropHover && !dropSource
                      ? dropPast
                        ? 'ring-2 ring-inset ring-red-400 bg-red-50'
                        : 'ring-2 ring-inset ring-blue-500 bg-blue-100/70'
                      : ''
                  } active:bg-gray-50`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div
                    className={`mb-0.5 inline-flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center text-[11px] sm:text-[13px] font-semibold ${
                      cell.isToday
                        ? 'rounded-full bg-blue-600 text-white'
                        : cell.inMonth
                          ? 'text-gray-700'
                          : 'text-gray-300'
                    }`}
                  >
                    {cell.day}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {holidayName ? (
                      <span
                        className={`min-w-0 truncate rounded-full bg-green-100 px-1 sm:px-1.5 text-[8px] sm:text-[10px] font-semibold leading-[13px] sm:leading-4 ${
                          cell.inMonth ? 'text-green-800' : 'text-green-800/50'
                        }`}
                        title={holidayName}
                      >
                        {holidayName}
                      </span>
                    ) : null}
                    {visible.map((item) => renderItem(item))}
                    {overflow > 0 ? (
                      <span className="px-1 text-[10px] sm:text-[11px] font-semibold text-gray-500">
                        +{overflow} more
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {movingItem && typeof document !== 'undefined'
        ? createPortal(
            <>
              {dragPoint ? (
                <div
                  className="fixed pointer-events-none flex max-w-[220px] items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold text-white shadow-xl"
                  style={{
                    zIndex: 100150,
                    left: dragPoint.x,
                    top: dragPoint.y,
                    transform: 'translate(-50%, -120%) scale(1.04)',
                    backgroundColor: movingItem.bgColor,
                  }}
                >
                  <Move className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{movableLabel(movingItem)}</span>
                </div>
              ) : null}
              <div
                className="fixed inset-x-0 flex justify-center px-3 pointer-events-none"
                style={{ zIndex: 100140, top: 'calc(env(safe-area-inset-top, 0px) + 64px)' }}
              >
                <div
                  className="pointer-events-auto flex max-w-full items-center gap-3 rounded-2xl bg-blue-600 px-4 py-2.5 text-white shadow-2xl"
                  role="status"
                >
                  <Move className="h-4 w-4 shrink-0 animate-pulse" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">Moving: {movableLabel(movingItem)}</p>
                    <p className="text-[11px] text-white/75">
                      {dragPoint
                        ? 'Release over a day to move it'
                        : 'Tap a day to move it · keeps the same time'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={exitMoveMode}
                    className="shrink-0 rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/25 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>,
            document.body,
          )
        : null}

      {pastRejectRect && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed pointer-events-none animate-create-slot-in"
              style={{
                zIndex: 40,
                top: pastRejectRect.top,
                left: pastRejectRect.left,
                width: Math.max(pastRejectRect.width, 2),
                height: Math.max(pastRejectRect.height, 2),
                borderRadius: 6,
                background: 'rgba(239, 68, 68, 0.16)',
                border: '1.5px dashed rgba(239, 68, 68, 0.75)',
                boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.14)',
              }}
            />,
            document.body,
          )
        : null}

      <CalendarCreateChoiceMenu
        isOpen={choiceOpen && !!choiceDate}
        onClose={closeCreateMenu}
        options={choiceOptions}
        contextLabel={choiceDate ? formatChoiceDate(choiceDate) : undefined}
        footerNote={footerNote}
        loading={loadingChannels && choiceOptions.length <= 1}
        anchorRect={choiceAnchorRect}
        anchorSlotKey={choiceAnchorKey}
      />

      <CreateGbpPostModal
        open={createGbpOpen}
        onClose={() => {
          setCreateGbpOpen(false);
          setCreateScheduleLocal(null);
        }}
        initialScheduleLocal={createScheduleLocal}
        onSuccess={() => {
          onGbpChanged?.();
          void refreshChannels();
        }}
      />

      {createSocialPlatform ? (
        <CreateSocialPostModal
          open
          platform={createSocialPlatform}
          onClose={() => {
            setCreateSocialPlatform(null);
            setCreateScheduleLocal(null);
          }}
          initialScheduleLocal={createScheduleLocal}
          onSuccess={() => {
            onSocialChanged?.();
            void refreshChannels();
          }}
        />
      ) : null}

      {GBP_ENABLED && businessId ? (
        <ControlCentreGbpPostPreviewModal
          open={!!detailsGbpPost}
          businessId={businessId}
          post={detailsGbpPost}
          onClose={() => setDetailsGbpPost(null)}
          onChanged={() => onGbpChanged?.()}
        />
      ) : null}

      {SOCIAL_ENABLED && businessId ? (
        <ControlCentreSocialPostPreviewModal
          open={!!detailsSocialPost}
          businessId={businessId}
          post={detailsSocialPost}
          onClose={() => setDetailsSocialPost(null)}
          onChanged={() => onSocialChanged?.()}
        />
      ) : null}
    </div>
  );
}
